import { Select } from "@jhool-io/fe-components";
import { useState } from "react";
import type { ReactNode } from "react";
import type { FormatOptionLabelMeta, InputActionMeta } from "react-select";
import { useDebounce } from "../../../../hooks/helpers";
import { useFetchDiagnosisCodes } from "../../../../hooks/queries";
import {
    DiagnosisCodeOption,
    getActiveDiagnosisCodeOptions,
    getDiagnosisDefaultValue,
    getDiagnosisDefaultValues,
} from "../../../../utils/helpers/diagnosis";

type DiagnosisCodeValue = string | string[] | null | undefined;

interface DiagnosisCodeSelectProps {
    label: string;
    value: DiagnosisCodeValue;
    onChange(value: string | string[] | null): void;
    errorText?: string;
    placeholder?: string;
    isRequired?: boolean;
    isOptional?: boolean;
    isMulti?: boolean;
    isClearable?: boolean;
    isDisabled?: boolean;
    hideSelectedOptions?: boolean;
    closeMenuOnSelect?: boolean;
    isLongListInDialog?: boolean;
    formatOptionLabel?(
        option: unknown,
        meta: FormatOptionLabelMeta<unknown>
    ): ReactNode;
}

export default function DiagnosisCodeSelect({
    label,
    value,
    onChange,
    errorText,
    placeholder = "",
    isRequired,
    isOptional,
    isMulti = false,
    isClearable,
    isDisabled,
    hideSelectedOptions,
    closeMenuOnSelect,
    isLongListInDialog,
    formatOptionLabel,
}: DiagnosisCodeSelectProps) {
    const [searchValue, setSearchValue] = useState("");
    const search = useDebounce(searchValue.trim(), 500);
    const {
        data,
        isLoading,
        isFetching,
        error,
        cachedDiagnosisCodesForSelectedValues,
    } = useFetchDiagnosisCodes(undefined, search);

    const handleInputChange = (
        inputValue: string,
        { action }: InputActionMeta
    ) => {
        if (action === "input-change") {
            setSearchValue(inputValue);
            return;
        }

        if (action === "set-value" || action === "menu-close") {
            setSearchValue("");
        }
    };

    const handleChange = (selection: unknown) => {
        if (isMulti) {
            onChange(
                ((selection ?? []) as DiagnosisCodeOption[]).map(
                    (option) => option.value
                )
            );
            return;
        }

        onChange((selection as DiagnosisCodeOption | null)?.value ?? null);
    };

    const selectedValue = isMulti
        ? getDiagnosisDefaultValues(
              Array.isArray(value) ? value : [],
              cachedDiagnosisCodesForSelectedValues,
              search.length >= 2 || isFetching
          )
        : getDiagnosisDefaultValue(
              typeof value === "string" ? value : null,
              cachedDiagnosisCodesForSelectedValues,
              search.length >= 2 || isFetching
          );

    let currentPlaceholder = placeholder;
    if (isLoading) {
        currentPlaceholder = "Loading...";
    } else if (error) {
        currentPlaceholder = "Error loading diagnosis";
    }

    return (
        <Select
            label={label}
            placeholder={currentPlaceholder}
            value={selectedValue}
            options={getActiveDiagnosisCodeOptions(data?.data)}
            onInputChange={handleInputChange}
            onChange={handleChange}
            filterOption={search.length >= 2 ? () => true : undefined}
            isLoading={isFetching}
            isDisabled={
                isDisabled ||
                ((isLoading || Boolean(error)) && search.length < 2)
            }
            hasError={Boolean(errorText)}
            errorText={errorText}
            multiHasValues={isMulti && Boolean(value?.length)}
            isSearchable
            isMulti={isMulti}
            isRequired={isRequired}
            isOptional={isOptional}
            isClearable={isClearable}
            hideSelectedOptions={hideSelectedOptions}
            closeMenuOnSelect={closeMenuOnSelect}
            isLongListInDialog={isLongListInDialog}
            formatOptionLabel={formatOptionLabel}
        />
    );
}
